import React, { useEffect, useState } from "react"
import { useAtom } from "jotai"
import { toast } from "react-toastify"
import { userAtom } from "../../store"
import { blockSeller, getBlockedSellerIds, unblockSeller } from "../../firebase/blocks"
import DialogConfirmation from "../dialogs/DialogConfirmation"

/**
 * Blocks or unblocks a seller on behalf of the signed-in buyer.
 *
 * `seller` is the ACCOUNT behind the listing, so one block covers the
 * seller's own listings and every Shopify store they own. For a Shopify item
 * that account is the store's current owner -- resolveSellerIdentity()'s
 * uid, never the product's seller_id, which is the store's role id.
 *
 * The blocked state is loaded here rather than by each caller, and reported
 * back through `onChange` so a page can also gate its own purchase controls
 * without repeating the lookup.
 *
 * `variant` picks the presentation: "button" is the standalone control on
 * the seller's profile, "link" the small red text link that sits beside
 * View Profile in a listing's seller panel.
 */
const BlockSellerButton = ({ seller, onChange, variant = "button", className = "" }) => {
    const [userData] = useAtom(userAtom)
    const [blocked, setBlocked] = useState(null)
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [saving, setSaving] = useState(false)

    const buyerUid = userData?.id
    const sellerUid = seller?.id
    const sellerName = seller?.displayName || "this seller"
    // Your own storefront, or a store with no owner assigned: there is no
    // account to block.
    const blockable = Boolean(buyerUid && sellerUid && sellerUid !== buyerUid)

    useEffect(() => {
        if (!blockable) {
            setBlocked(false)
            return
        }

        getBlockedSellerIds(buyerUid).then((ids) => setBlocked(ids.has(sellerUid)))
    }, [buyerUid, sellerUid, blockable])

    useEffect(() => {
        if (blocked !== null && onChange) onChange(blocked)
    }, [blocked])

    const applyBlock = async () => {
        setSaving(true)
        const ok = await blockSeller(buyerUid, seller)
        setSaving(false)
        setShowConfirmation(false)

        if (!ok) {
            toast.error("Could not block this seller. Please try again.")
            return
        }

        setBlocked(true)
        toast.success(`${sellerName} is now blocked. Their listings are hidden from the marketplace.`)
    }

    const applyUnblock = async () => {
        setSaving(true)
        const ok = await unblockSeller(buyerUid, sellerUid)
        setSaving(false)

        if (!ok) {
            toast.error("Could not unblock this seller. Please try again.")
            return
        }

        setBlocked(false)
        toast.success(`${sellerName} is unblocked.`)
    }

    // Nothing to offer until the current state is known, and nothing to
    // offer at all when there is no account to block.
    if (!blockable || blocked === null) return null

    const label = saving
        ? (blocked ? "Unblocking..." : "Blocking...")
        : (blocked ? "Unblock Seller" : "Block Seller")

    // Mirrors the View Profile link it sits next to -- same size, same
    // underline -- in red, since it is the destructive one of the pair.
    const linkClassName = `text-[10px] lg:text-xs flex items-center underline bg-transparent ${blocked ? "text-primary" : "text-[#E5484D]"
        }`

    const standaloneClassName = `flex items-center justify-center border-[1px] text-sm sm:px-8 px-4 py-3 transition-all ${blocked
        ? "border-gray text-white bg-[#2E2E2E] hover:border-primary"
        : "border-[#E5484D] text-[#E5484D] bg-transparent hover:bg-[#E5484D] hover:text-white"
        }`

    return (
        <>
            <DialogConfirmation
                open={showConfirmation}
                onClose={() => setShowConfirmation(false)}
                type="confirm"
                title={`Block ${sellerName}?`}
                message="Their listings, including every Shopify store they run, will be hidden from the marketplace. You can unblock them any time from Blocked Sellers in your profile."
                buttonText={saving ? "Blocking..." : "Block Seller"}
                onButtonClick={saving ? () => { } : applyBlock}
            />

            <button
                className={`${variant === "link" ? linkClassName : standaloneClassName} ${saving ? "opacity-50" : ""} ${className}`}
                onClick={blocked ? applyUnblock : () => setShowConfirmation(true)}
                disabled={saving}
            >
                {label}
            </button>
        </>
    )
}

export default BlockSellerButton
